import React, { useEffect, useState } from "react";
import { Client } from "../../interfaces/client";
import { ClientNotificationType } from "../../interfaces/clientNotification";
import {
  getEmailPreferences,
  getNotificationTypes,
  saveEmailPreferences,
} from "../../services/notificationsService";
import ToolTip from "../component-tools/ToolTip";

interface EmailPreferencesProps {
  client: Client;
  onClose: () => void;
  onFrequencyChange: (newFrequencyID: number) => void;
}

const EmailPreferences: React.FC<EmailPreferencesProps> = ({
  client,
  onFrequencyChange,
  onClose,
}) => {
  const [notificationTypes, setNotificationTypes] = useState<
    ClientNotificationType[]
  >([]);
  const [optOutTypeIDs, setOptOutTypeIDs] = useState<number[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [emailFrequency, setEmailFrequency] = useState<number>(0);

  useEffect(() => {
    const loadEmailPreferences = async () => {
      try {
        const allEmailTypes = await getNotificationTypes();
        const filteredTypes = allEmailTypes.filter(
          (type) =>
            type.clientNotificationTypeID !== 3 &&
            type.clientNotificationTypeID !== 8 &&
            type.clientNotificationTypeID !== 7 &&
            type.clientNotificationTypeID !== 6
        );
        setNotificationTypes(filteredTypes);

        const response = await getEmailPreferences(client.clientID);
        const initialOptOuts = response.map((p) => p.clientNotificationTypeID);
        setOptOutTypeIDs(initialOptOuts);

        setEmailFrequency(client.emailFrequencyID ?? 0);
      } catch (error) {
        setErrorMessage("Failed to load email preferences.");
      } finally {
        setLoadingSpinner(false);
      }
    };

    loadEmailPreferences();
  }, [client.clientID, client.emailFrequencyID]);

  const handleToggle = (typeID: number) => {
    setOptOutTypeIDs((prev) =>
      prev.includes(typeID)
        ? prev.filter((id) => id !== typeID)
        : [...prev, typeID]
    );
  };

  const handleSaveSelection = async () => {
    try {
      setLoadingSpinner(true);
      await saveEmailPreferences(
        client.clientID,
        optOutTypeIDs,
        emailFrequency
      );
      onFrequencyChange(emailFrequency);
      onClose();
    } catch (error) {
      console.error("Error saving email preferences:", error);
      setErrorMessage("Error saving email preferences.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  return (
    <>
      <div className="background-mask" onClick={() => onClose()}></div>
      <div className="filter-popup">
        <div className="close-button-container">
          <button className="close-button" onClick={() => onClose()}>
            <i className="bi bi-x-circle"></i>
          </button>
        </div>
        <div className="filter-header">
          <h2>Your Email Preferences</h2>
          <div>Un-check to stop receiving a specific email.</div>
          <div>Click save before exiting to save your preferences.</div>
        </div>
        <br></br>
        <div className="dialog-container">
          {notificationTypes.map((type) => {
            const optedOut = optOutTypeIDs.includes(
              type.clientNotificationTypeID
            );
            return (
              <div key={type.clientNotificationTypeID} className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={!optedOut}
                    onChange={() => handleToggle(type.clientNotificationTypeID)}
                  />{" "}
                  {type.typeName}
                </label>
              </div>
            );
          })}
          <br></br>
          <div className="form-group">
            <div style={{ display: "flex", gap: "5px" }}>
              <label>Email Frequency:</label>
              <ToolTip
                tooltipText="Choose the frequency in which opt-ed in email notifications are delivered to contacts with notifications enabled."
                placement="top"
                className="info-icon-spec"
              />
            </div>
            <select
              className="form-control"
              value={emailFrequency}
              onChange={(e) => setEmailFrequency(parseInt(e.target.value))}
            >
              <option value={0}>Immediately</option>
              <option value={1}>Daily Summary</option>
              <option value={2}>Weekly Summary</option>
            </select>
          </div>
          {errorMessage && <div className="error">{errorMessage}</div>}
        </div>
        <div className="filter-actions">
          <button
            className="smbtn btn-green"
            onClick={() => handleSaveSelection()}
          >
            Save
          </button>
        </div>
      </div>
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </>
  );
};

export default EmailPreferences;
